import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, File as FileIcon, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Uploads files to /api/files/upload and manages a list of file IDs.
 * onChange(nextIds: string[]) is called after each successful upload/removal.
 */
export default function FileUploader({ value = [], onChange, purpose = "general", accept = "*/*", label = "Upload files", testid = "file-uploader", multiple = true }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [metas, setMetas] = useState(value.map((id) => ({ id, filename: "file", uploading: false })));

  React.useEffect(() => {
    // Sync value from parent (initial load)
    if (value.length && !metas.length) {
      Promise.all(value.map(async (id) => {
        try { const { data } = await api.get(`/files/${id}/meta`); return { id, filename: data.original_filename }; }
        catch { return { id, filename: "file" }; }
      })).then(setMetas);
    }
    // eslint-disable-next-line
  }, [value]);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("purpose", purpose);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, filename: data.filename });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload failed for ${f.name}`);
      }
    }
    const next = [...metas, ...uploaded];
    setMetas(next);
    onChange?.(next.map((m) => m.id));
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => {
    const next = metas.filter((m) => m.id !== id);
    setMetas(next);
    onChange?.(next.map((m) => m.id));
  };

  return (
    <div data-testid={testid} className="space-y-2">
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-btn`}>
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {label}
      </Button>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" onChange={handle} />
      {metas.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {metas.map((m) => (
            <li key={m.id} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 bg-white" data-testid={`${testid}-item-${m.id}`}>
              <FileIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 truncate">{m.filename}</span>
              <button type="button" onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`${testid}-remove-${m.id}`}>
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
